import { IFlowTheme, IVendorEmail, IImage } from '~/types/model.types';
import { Company } from '~/models/company.model';
import { CompanyAdapter } from '~/services/adapters/company.adapter';
import { arrayToHashMapById } from '~/utils/server-response.utils';
import {
    IFileDetailsPayload,
    ITag,
    TeamRole,
    TImageUploadType
} from '~/types/server.types';

export class CompanyService extends CompanyAdapter {
    async getEmailTemplate(
        companyId: string,
        templateId: string
    ): Promise<IVendorEmail> {
        const { vendor_emails } = await super._getEmailTemplate(
            companyId,
            templateId
        );

        return vendor_emails.find(t => t._id === templateId);
    }

    async updateCompanyFlowTheme(companyId: string, theme: IFlowTheme) {
        const { company_flow_theme } = await super._updateCompanyFlowTheme(
            companyId,
            theme
        );

        return company_flow_theme;
    }

    async updateDisableClientPvlRequests(
        companyId: string,
        disable_client_pvl_requests: boolean
    ) {
        return super.updateDisableClientPvlRequests(
            companyId,
            disable_client_pvl_requests
        );
    }

    async getCompanyTagHash(): Promise<{ [tagId: string]: ITag }> {
        const tags = await super.getCompanyTags();
        const tagHash = {};
        tags.forEach(tag => {
            tagHash[tag._id] = tag;
        });
        return tagHash;
    }

    async getSortedCompanyTagsHash(): Promise<{ [tagId: string]: ITag }> {
        const tags = await super.getCompanyTags();
        const tagsHash = arrayToHashMapById(
            tags.sort((tagA, tagB) =>
                tagA.tag_name.localeCompare(tagB.tag_name)
            )
        );
        return tagsHash;
    }

    async getCoverImages(
        companyId: string,
        page: number,
        perPage: number
    ): Promise<{ cover_images: IImage[]; total_count: number }> {
        return super.getCoverImages(companyId, page, perPage);
    }

    async updateCompany(props: {
        companyId: string;
        company: Partial<Company>;
    }) {
        return super._updateCompany(props);
    }

    async updateCompanySettingsPermissions(
        companyId: string,
        role: TeamRole
    ): Promise<{ role: TeamRole }> {
        return super.updateCompanySettingsPermissions(companyId, role);
    }

    async addAssetsToServer({
        fileDetails,
        companyId
    }: {
        fileDetails: IFileDetailsPayload;
        companyId: string;
    }) {
        const updateAssetsInDBResponse = await this.updateAssetsInDB({
            companyId,
            fileDetails
        });

        const company_assets = updateAssetsInDBResponse.company_assets;
        const cover_images = updateAssetsInDBResponse.cover_images;

        return {
            company_assets,
            cover_images
        };
    }

    async addAssetsToServerWithSlimResponse({
        fileDetails,
        companyId
    }: {
        fileDetails: IFileDetailsPayload;
        companyId: string;
    }) {
        const updateAssetsInDBResponse =
            await this.updateAssetsInDBWithSlimResponse({
                companyId,
                fileDetails
            });

        const company_assets = updateAssetsInDBResponse.company_assets;
        const cover_images = updateAssetsInDBResponse.cover_images;

        return {
            company_assets,
            cover_images
        };
    }

    async addImageToServer({
        image,
        companyId,
        uploadType
    }: {
        image: IImage;
        companyId: string;
        uploadType: TImageUploadType;
    }) {
        const updateImageInDBResponse = await this.updateImageInDB({
            companyId,
            image,
            uploadType
        });

        if (uploadType === 'icon') {
            return updateImageInDBResponse.icon_image;
        }

        return updateImageInDBResponse.cover_images.find(
            currImg =>
                currImg.cloudinary_public_id === image.cloudinary_public_id
        );
    }
}
